class ArchiveProductLazyLoader {
    private overview!: HTMLElement;
    private products!: HTMLUListElement;
    private sentinel!: HTMLElement;
    private observer!: IntersectionObserver;
    private loading: boolean = false;

    constructor(overview: HTMLElement) {
        const products = overview.querySelector<HTMLUListElement>('ul.products');
        const sentinel = overview.querySelector<HTMLElement>('.archive-load-more');

        if (!products || !sentinel) return;
        if (sentinel.dataset.lazyInitialized === 'true') return;

        this.overview = overview;
        this.products = products;
        this.sentinel = sentinel;
        this.init();
    }

    private init(): void {
        this.sentinel.dataset.lazyInitialized = 'true';

        // The skeleton placeholders double as the trigger
        this.observer = new IntersectionObserver((entries) => {
            if (entries.some(entry => entry.isIntersecting)) {
                this.loadMore();
            }
        }, {
            rootMargin: '600px 0px',
        });

        this.renderPlaceholders();
    }

    // How many products fit in one batch
    private batchSize(): number {
        return parseInt(this.sentinel.dataset.perPage ?? '24', 10);
    }

    private currentParams(): URLSearchParams {
        const params = new URLSearchParams(window.location.search);

        params.set('action', 'minitrekkers_load_products');
        params.set('offset', this.sentinel.dataset.offset ?? '0');
        params.set('per_page', this.sentinel.dataset.perPage ?? '24');
        params.set('taxonomy', this.overview.dataset.taxonomy ?? '');
        params.set('term_id', this.overview.dataset.termId ?? '');

        return params;
    }

    // Number of products still waiting to be loaded.
    private remaining(): number {
        const offset = parseInt(this.sentinel.dataset.offset ?? '0', 10);
        const total = parseInt(this.sentinel.dataset.total ?? '0', 10);

        return Math.max(0, total - offset);
    }

    private removePlaceholders(): void {
        this.products.querySelectorAll('.product-placeholder').forEach(el => {
            this.observer.unobserve(el);
            el.remove();
        });
    }

    // Render skeleton products at the end of the grid
    private renderPlaceholders(): void {
        this.removePlaceholders();

        const count = Math.min(this.batchSize(), this.remaining());

        if (count <= 0) {
            this.sentinel.remove();
            return;
        }

        const placeholder = `
            <li class="product product-placeholder" aria-hidden="true">
                <div class="product-top">
                    <div class="product-image"></div>
                    <div class="product-title"></div>
                </div>
                <div class="product-content">
                    <div class="product-subtitle"></div>
                    <div class="product-info">
                        <div class="product-info-item"></div>
                        <div class="product-info-item"></div>
                    </div>
                </div>
                <div class="product-bottom">
                    <div class="price"></div>
                </div>
            </li>`;

        this.products.insertAdjacentHTML('beforeend', placeholder.repeat(count));
        this.products.querySelectorAll('.product-placeholder').forEach(el => this.observer.observe(el));
    }

    private async loadMore(): Promise<void> {
        if (this.loading) return;

        const offset = parseInt(this.sentinel.dataset.offset ?? '0', 10);
        const total = parseInt(this.sentinel.dataset.total ?? '0', 10);

        if (offset >= total) {
            this.removePlaceholders();
            this.sentinel.remove();
            return;
        }

        this.loading = true;

        try {
            const response = await fetch(this.overview.dataset.ajaxUrl ?? '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                },
                body: this.currentParams(),
            });

            if (!response.ok) return;

            const data = await response.json();
            const html = data?.data?.html ?? '';
            const count = parseInt(data?.data?.count ?? '0', 10);

            if (!html || count <= 0) {
                this.removePlaceholders();
                this.sentinel.remove();
                return;
            }

            // Drop the placeholders before inserting the real products
            this.removePlaceholders();
            this.products.insertAdjacentHTML('beforeend', html);

            const nextOffset = offset + count;
            this.sentinel.dataset.offset = nextOffset.toString();

            this.renderPlaceholders();
        } finally {
            this.loading = false;
        }
    }
}

const initArchiveProductLazyLoaders = () => {
    document.querySelectorAll<HTMLElement>('[data-product-lazy]').forEach(overview => {
        new ArchiveProductLazyLoader(overview);
    });
};

window.addEventListener('load', initArchiveProductLazyLoaders);
window.addEventListener('archive-products-updated', initArchiveProductLazyLoaders);
